// Vendors
import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Components
import { Loader } from '../../../components';

const Appliances = ({
  projectData,
  handleInputChange,
  updateSpecificProjectDataProperty
}) => {
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState({});
  const [totalIncentive, setTotalIncentive] = useState(
    projectData.appliance_total_incentives ?? 0
  );
  const [region, setRegion] = useState(null);
  const [qualifyingAppliances, setQualifyingAppliances] = useState([]);
  const [newConstructionRegionBonus, setNewConstructionRegionBonus] = useState(
    0
  );
  const [regionBonusMessage, setRegionBonusMessage] = useState(null);
  const [bonusAppliance, setBonusAppliance] = useState(null);
  let { construction_type } = projectData;

  const changedFromAppliances = [
    'cooktop_range', 
    'oven', 
    'clothes_dryer', 
    'standby_generator', 
    'fireplace_gas_log', 
    'piping_propane_gas', 
    'furnace'
  ];

  const resetChangedFromFields = () => {
    changedFromAppliances.forEach(appliance => updateSpecificProjectDataProperty(`appliance_${appliance}_changed_from`, ''));
  }

  let _isSubscribed = true;

  const handleCheckBoxChange = (propName) => {
    let appliance = propName.substring(propName.indexOf('_') + 1);

    // clear changed from value if appliance is unchecked
    if (changedFromAppliances.includes(appliance) && projectData[propName] > 0) {
      let key = `${propName}_changed_from`;
      updateSpecificProjectDataProperty(key, '');
    }

    updateSpecificProjectDataProperty(
      propName,
      projectData[propName] > 0 ? 0 : 1
    );

    getNewConstructionRegionalBonus();
    getTotalIncentiveAmount();
  };

  useEffect(() => {
    getNewConstructionRegionalBonus();
    getTotalIncentiveAmount();
  }, [construction_type, qualifyingAppliances]);

  // remove "changed from" values if changing from remodel to new construction
  useEffect(() => {
    resetChangedFromFields();
  }, [construction_type])

  const handleCountChange = (propName, sum) => {
    let newVal = 0;
    if (sum > 0) {
      newVal = parseInt(projectData[propName]) + 1;
    } else {
      newVal = parseInt(projectData[propName]) - 1;
      newVal = newVal >= 0 ? newVal : 0;
    }

    getNewConstructionRegionalBonus();
    updateSpecificProjectDataProperty(propName, newVal);
    getTotalIncentiveAmount();
  };

  const getNewConstructionRegionalBonus = () => {
    // Only applies to 'New' constuctions.
    if (construction_type !== 'New') {
      setNewConstructionRegionBonus(0);
      setBonusAppliance(null);
      return;
    };

    let bonusAppliance = '';

    // Check if builder has a qualiying appliance selected, if so, bail.
    const qualifies = Object.entries(qualifyingAppliances).some((appliance) => {
      if (projectData[appliance[1]] > 0) {
        bonusAppliance = appliance[0];
        return true;
      }
    });

    // Bonus is 500 max., regardless of how many qualifying appliances builder selects.
    setNewConstructionRegionBonus(qualifies ? 500 : 0);
    setBonusAppliance(bonusAppliance);
  };

  const getTotalIncentiveAmount = () => {
    if (fields && !Object.keys(fields).length) return;

    const requiredApplianceFields = fields[0].fields;
    const additionalApplianceFields = fields[1].fields;
    let requiredFieldsTotal = 0;
    let additionalFieldsTotal = 0;
    let remodelFieldsTotal = 0;
    let grandTotal = 0;

    requiredApplianceFields.map(({ meta_name, amount }) => {
      const applianceCount = projectData[meta_name];

      if (applianceCount > 0) {
        requiredFieldsTotal += amount * applianceCount;
      }
    });

    additionalApplianceFields.map(({ meta_name, amount }) => {
      const applianceCount = projectData[meta_name];

      if (applianceCount > 0) {
        additionalFieldsTotal += amount * applianceCount;
      }
    });

    if (construction_type == 'Remodel') {
      // Having 1 appliance fuel-switch, is enough to qualify for the $500 bonus,
      // so we're only checking for 1 here..
      const firstFuelSwitch = additionalApplianceFields.find(
        ({ meta_name }) => projectData[`${meta_name}_changed_from`] !== ''
      );
      remodelFieldsTotal = firstFuelSwitch !== undefined ? 500 : 0;
    }

    grandTotal = requiredFieldsTotal + additionalFieldsTotal;
    grandTotal = grandTotal > 1000 ? 1000 : grandTotal;

    if (construction_type == 'New') {
      grandTotal += newConstructionRegionBonus;
    } else {
      grandTotal += remodelFieldsTotal;
    }

    // Reset to 0 if no required appliances were selected.
    if (requiredFieldsTotal === 0) {
      grandTotal = 0;
    }

    setTotalIncentive(grandTotal);
    updateSpecificProjectDataProperty('appliance_total_incentives', grandTotal);
  };

  const getSelectedRemodelAppliances = (index) => {
    // Appliances are in the 2nd field section group of 'fields' (index=1).
    const rows = fields[index]['fields'].map(({ meta_name, label }, index) => {
      const projectDataProp = projectData[meta_name];

      // We're only interested in selected Appliances.
      if (projectDataProp === '' || projectDataProp == 0) return;
      const propName = `${meta_name}_changed_from`;
      const projectDataSelectProp = projectData[propName];

      return (
        <div key={index} className="appliance-form__row">
          <div className="form__field appliance-form__item">
            <label className="form__checkbox" htmlFor={`remodel_${meta_name}`}>
              <input
                id={`remodel_${meta_name}`}
                type="checkbox"
                name={`remodel_${propName}`}
                checked={
                  projectDataSelectProp !== '' && projectDataSelectProp !== '0'
                }
                onChange={(event) => {
                  updateSpecificProjectDataProperty(
                    propName,
                    projectDataSelectProp !== '' ? '' : 'Oil'
                  );
                  handleInputChange(event)
                  getTotalIncentiveAmount();
                }}
              />{' '}
              {label}
            </label>
          </div>
          <div className="appliance-form__amount">$500</div>
          <div className="appliance-form__remodel-select">
            <div className="form__select form__select--no-shadow">
              <select
                name={propName}
                value={projectDataSelectProp}
                onChange={(event) => {
                  handleInputChange(event);
                  getTotalIncentiveAmount();
                }}
              >
                <option value="">
                  -- Select fuel type that was switched from --
                </option>
                <option value="Oil">Oil to Propane</option>
                <option value="Electricity">Electricity to Propane</option>
              </select>
            </div>
          </div>
        </div>
      );
    });

    return rows;
  };

  useEffect(() => {
    if (projectData.state) {
      setLoading(true);
      _isSubscribed = true;
  
      const formData = new FormData();
      formData.append('action', 'wl_pip_get_appliances_fields');
      formData.append('state', projectData.state);
      formData.append('construction_type', construction_type);
  
      axios({
        method: 'post',
        url: window.wlPipConfig.ajaxUrl,
        data: formData
      })
        .then(
          ({
            data: {
              success,
              data: { fields, region, qualifying }
            }
          }) => {
            if (_isSubscribed) {
              if (success && fields) {
                setFields(fields);
                setRegion(region);

                if (Object.keys(qualifying).length) {
                  setQualifyingAppliances({ ...qualifying });
                }
              }
  
              setTimeout(() => setLoading(false), 100);
              _isSubscribed = false;
            }
          }
        )
        .catch((error) => {
          console.log(error);
  
          setLoading(false);
        });
  
      return () => (_isSubscribed = false);
    }
  }, [projectData.state, construction_type]);

  useEffect(() => getTotalIncentiveAmount(), [newConstructionRegionBonus]);

  if (loading) return <Loader />;

  return (
    <div id="pip-appliances">
      <div className="appliance-form u--font-size-lg">
        {fields.length &&
          Object.entries(fields).map((field_section, index) => {
            if (index === 2) return;

            const formRows = Object.entries(field_section[1].fields).map(
              (field_row, index) => {
                const { meta_name, label, amount } = field_row[1];
                const projectDataProp = projectData[meta_name];

                return (
                  <div key={index} className="appliance-form__row">
                    <div className="form__field appliance-form__item">
                      <label className="form__checkbox" htmlFor={meta_name}>
                        <input
                          id={meta_name}
                          type="checkbox"
                          name={meta_name}
                          checked={projectDataProp > 0}
                          onChange={(event) =>
                            handleCheckBoxChange(event.currentTarget.name)
                          }
                        />{' '}
                        {label}
                      </label>
                    </div>
                    <div
                      className={`appliance-form__count ${
                        projectDataProp > 0 ? 'appliance-form__count--show' : ''
                      }`}
                    >
                      <button
                        className="btn appliance-form__count__change"
                        onClick={() => handleCountChange(meta_name, -1)}
                      >
                        &ndash;
                      </button>
                      <span className="appliance-form__count__number">
                        {projectDataProp}
                      </span>
                      <button
                        className="btn appliance-form__count__change"
                        onClick={() => handleCountChange(meta_name, 1)}
                      >
                        +
                      </button>
                    </div>
                    <div className="appliance-form__amount">${amount}</div>
                  </div>
                );
              }
            );

            return (
              <div key={index}>
                <p
                  dangerouslySetInnerHTML={{ __html: field_section[1].intro }}
                  className="u--margin-bottom-10"
                />
                {formRows}
                <hr />
                {construction_type === 'Remodel' && fields.length && index < 2 && (
                  <>
                    {getSelectedRemodelAppliances(index)}
                    <hr />
                  </>
                )}
              </div>
            );
          })}

        {/* New bonus message, if applicable */}
        {construction_type === 'New' && bonusAppliance && region && (
          <div className="notice notice--success" role="alert">
            Your selection of {bonusAppliance} automatically qualifies you for
            the $500 {region} bonus.
          </div>
        )}
        {projectData.state ? (
          <>
            <div className="appliance-form__row">
              <div className="form__field appliance-form__item">
                <label className="form__checkbox" htmlFor="appliance_attestation">
                  <input
                    id="appliance_attestation"
                    type="checkbox"
                    name="appliance_attestation"
                    checked={projectData.appliance_attestation > 0}
                    onChange={(event) =>
                      handleCheckBoxChange(event.currentTarget.name)
                    }
                  />{' '}
                  I certify that the propane appliances selected in this application, for this address, have been installed.(required)
                </label>
              </div>
            </div>
            <hr />
            <div className="appliance-form__row u--margin-bottom-30">
              <span>Total Incentive</span>
              <strong>
                {parseInt(totalIncentive) > 0
                  ? `$${parseInt(totalIncentive).toLocaleString()}`
                  : '--'}
              </strong>
            </div>
            <p className="u--padding-left-10-xs-min">
              <a
                href="/pcip/incentives/"
                title="Incentives Overview"
                className="btn btn--lg btn--blue btn--link btn--has-arrow module-hover-root"
              >
                <span className="btn__content">
                  <span className="btn__text">View Incentives Details</span>
                  <span className="btn__arrow arrow"></span>
                </span>
              </a>
            </p>
          </>
        ) : (
          <p>Please select a state before selecting appliances.</p>
        )}
      </div>
    </div>
  );
};
export default Appliances;
